import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useSelector, useDispatch } from "react-redux";

//css
import "./Window.scoped.scss";

//components
import Sidebar from "./Sidebar/Sidebar";
import Topbar from "./Topbar/Topbar";

// common components
import ScrollToTop from "components/common/ScrollToTop/ScrollToTop";
import BallGridPulseLoader from "components/common/BallGridPulseLoader/BallGridPulseLoader";

// hooks
import useTab from "hooks/useTab";
import useRedirect from "hooks/useRedirect";
import useEventListener from "hooks/useEventListener";
import { WindowState } from "redux/reducers/windowReducer";

const DepartMenu = lazy(() => import("./Content/DepartMenu/DepartMenu"));
const Routes = lazy(() => import("./Content/Routes/Routes"));
const Fleet = lazy(() => import("./Content/Fleet/Fleet"));
const Order = lazy(() => import("./Content/Order/Order"));
const Fuel = lazy(() => import("./Content/Fuel/Fuel"));
const Staff = lazy(() => import("./Content/Staff/Staff"));
const AirlineSettings = lazy(() => import("./Content/AirlineSettings/AirlineSettings"));

const Window = () => {
	const [dimensions, setDimensions] = useState({ width: -1, height: -1 });
	const [visibleDimensions, setVisibleDimensions] = useState({ width: null, height: null });

	const topbarEnabled = useSelector(state => state.window.tabBarEnabled);
	const navEnabled = useSelector(state => state.window.sidebarEnabled);
	const windowOpen = useSelector(state => state.window.open);

	const redirect = useRedirect();
	const dispatch = useDispatch();

	//refs
	const windowRef = useRef(null);
	const topBarRef = useRef(null);
	const windowControllerRef = useRef(null);
	const scrollToTopRef = useRef(null);

	const classList = useWindowClassList();

	// hiding and showing navigation elements
	// useEffect(toggleNav, [navEnabled]);
	// useEffect(toggleTopBar, [topbarEnabled]);

	// ResizeObserver
	useEffect(() => {
		const observer = new ResizeObserver(([entry]) => setDimensions(entry.contentRect));
		const el = windowControllerRef.current;
		observer.observe(el);
		return () => observer.unobserve(el);
	}, []);

	useEffect(() => {
		const observer = new ResizeObserver(([entry]) => {
			dispatch({ type: "SET_WINDOW_HEIGHT", state: entry.borderBoxSize[0].blockSize });
		});

		const el = windowRef.current;
		observer.observe(el);
		return () => observer.unobserve(el);
	}, [dispatch]);

	// IntersectionObserver
	useEffect(() => {
		let threshold = [0];
		for (let i; threshold[threshold.length - 1] < 1; i++) {
			threshold.push(threshold[threshold.length - 1] + 0.01);
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				const { width, height } = entry.intersectionRect;
				if (width < 70) return;
				setVisibleDimensions({ width, height });
			},
			{ threshold }
		);

		const ref = windowRef.current;
		if (ref) observer.observe(ref);
		return () => ref && observer.unobserve(ref);
	}, [windowOpen]);

	useEffect(() => {
		const observer = new ResizeObserver(([entry]) => {
			scrollToTopRef?.current?.onWindowHeightChange(entry.contentRect.height);
		});

		const ref = windowRef.current;
		if (ref) observer.observe(ref);

		return () => observer.unobserve(ref);
	}, []);

	//event listeners
	useEventListener(window, "resize", () => {
		if (windowControllerRef.current == null) return;
		setDimensions(windowControllerRef.current.getBoundingClientRect());
	});

	return (
		<>
			<div className={classList.join(" ")} ref={windowRef}>
				<Sidebar ref={topBarRef} navEnabled={navEnabled} />
				<Topbar navEnabled={navEnabled} topbarEnabled={topbarEnabled} />

				<div ref={windowControllerRef} style={{ width: "100%", position: "relative" }}>
					<Suspense fallback={<BallGridPulseLoader center={true} color="hsl(215, 50%, 23%)" />}>
						<WindowController size={dimensions} visibleDimensions={visibleDimensions} />
					</Suspense>
				</div>
			</div>
			{window.innerWidth > 500 && (
				<div className={windowOpen === WindowState.OPEN ? "overlay" : "overlay hide"} onClick={() => redirect("")} />
			)}
			<ScrollToTop ref={scrollToTopRef} />
		</>
	);
};

const WindowController = ({ visibleDimensions }) => {
	const tab = useTab("", true);
	const subTab = useTab("*", true);

	const defaultJsx = <p className="error">Nie wybrano zakładki lub komponent nie istnieje!</p>;

	// Maybe useless
	if (!tab) return defaultJsx;

	if (subTab && subTab.isSeparate) {
		switch (subTab.id) {
			case "fleet":
				return <Fleet />;

			case "order":
				return <Order />;

			case "Routes":
				return <Routes />;

			default:
				return defaultJsx;
		}
	}

	switch (tab.id) {
		case "departIcon":
			return <DepartMenu visibleDimensions={visibleDimensions} />;

		case "airlineSettingsIcon":
			return <AirlineSettings />;

		case "staffIcon":
			return <Staff />;

		case "fuelIcon":
			return <Fuel />;

		default:
			return defaultJsx;
	}
};

function useWindowClassList() {
	const topbarEnabled = useSelector(state => state.window.tabBarEnabled);
	const navEnabled = useSelector(state => state.window.sidebarEnabled);
	const windowOpen = useSelector(state => state.window.open);

	const __THEME__ = process.env.__THEME__;

	const classList = ["window"];

	if (__THEME__ === "retro") {
		classList.push("retro");
	}

	if (!topbarEnabled) {
		classList.push("window--disable_topbar");
	}

	if (!navEnabled) {
		classList.push("window--disable_nav");
	}

	if (windowOpen === WindowState.HALF_OPEN) {
		classList.push("window--half_hidden");
	} else if (windowOpen === WindowState.CLOSED) {
		classList.push("window--hidden");
	}

	return classList;
}

export default Window;
